export const UNIT_TO_CM = { mm: 0.1, cm: 1, m: 100, in: 2.54, ft: 30.48 };

export const LIQUIDS = [
  { name: "Avtur", density: 0.78, color: "#FACC15" },
  { name: "B50", density: 0.84, color: "#84CC16" },
  { name: "BioDex", density: 0.84, color: "#10B981" },
  { name: "HSD", density: 0.83, color: "#F59E0B" },
  { name: "MFO", density: 0.95, color: "#92400E" },
  { name: "Custom", density: 1.0, color: "#06B6D4" },
  { name: "Mixture", density: 0, color: "#A78BFA" },
];

export const PRESETS = [
  { name: "1,000L Vertical B50 Tank", orientation: "vertical", shape: "circular", unit: "cm", diameter: "100", tankLength: "128", fill: "96", liquid: "B50" },
  { name: "5,000L Horizontal HSD Vessel", orientation: "horizontal", shape: "circular", unit: "cm", diameter: "150", tankLength: "283", fill: "90", liquid: "HSD" },
  { name: "10,000L Elliptical MFO Tanker", orientation: "horizontal", shape: "elliptical", unit: "cm", width: "200", height: "140", tankLength: "450", fill: "60", liquid: "MFO" },
  { name: "IBC Tote 1,000L BioDex", orientation: "vertical", shape: "circular", unit: "cm", diameter: "100", tankLength: "120", fill: "60", liquid: "BioDex" },
  { name: "2,000L Rectangular B50 Basin", orientation: "vertical", shape: "rectangular", unit: "cm", width: "100", height: "100", tankLength: "200", fill: "100", liquid: "B50" },
  { name: "12,000L Stadium HSD Tank", orientation: "horizontal", shape: "stadium", unit: "cm", width: "220", height: "140", tankLength: "450", fill: "70", liquid: "HSD" },
];

export const LITER_CONVERSIONS = [
  { key: "us-gal", label: "US Gallons", perLiter: 1 / 3.785411784 },
  { key: "imp-gal", label: "Imperial Gallons", perLiter: 1 / 4.54609 },
  { key: "m3", label: "Cubic Meters", perLiter: 1 / 1000 },
  { key: "bbl", label: "Barrels (bbl)", perLiter: 1 / 158.987294928 },
];

const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

function circleSegmentArea(r, h) {
  if (h <= 0) return 0;
  if (h >= 2 * r) return Math.PI * r * r;
  return r * r * Math.acos((r - h) / r) - (r - h) * Math.sqrt(Math.max(0, 2 * r * h - h * h));
}

export function ellipticalSegmentArea(a, b, h) {
  if (h <= 0) return 0;
  if (h >= 2 * b) return Math.PI * a * b;
  return (a / b) * circleSegmentArea(b, h);
}

export function fullCrossArea(shape, wCm, hCm) {
  if (shape === "circular") return Math.PI * (wCm / 2) ** 2;
  if (shape === "elliptical") return Math.PI * (wCm / 2) * (hCm / 2);
  if (shape === "rectangular") return wCm * hCm;
  const L = Math.max(wCm, hCm);
  const S = Math.min(wCm, hCm);
  return (L - S) * S + Math.PI * (S / 2) ** 2;
}

export function filledCrossArea(shape, wCm, hCm, h) {
  if (shape === "circular") return circleSegmentArea(wCm / 2, h);
  if (shape === "elliptical") return ellipticalSegmentArea(wCm / 2, hCm / 2, h);
  if (shape === "rectangular") return wCm * clamp(h, 0, hCm);
  const H = Math.min(wCm, hCm);
  const straight = Math.max(wCm, hCm) - H;
  const hh = clamp(h, 0, H);
  return straight * hh + circleSegmentArea(H / 2, hh);
}

export function getGeometry({ orientation, shape, diameter, width, height, tankLength, unit }) {
  if (shape === "calibration") return null;
  const u = UNIT_TO_CM[unit] || 1;
  const d = parseFloat(diameter);
  const w = parseFloat(width);
  const h = parseFloat(height);
  const L = parseFloat(tankLength);
  let wCm;
  let hCm;
  if (shape === "circular") {
    if (!(d > 0)) return null;
    wCm = d * u;
    hCm = d * u;
  } else {
    if (!(w > 0) || !(h > 0)) return null;
    wCm = w * u;
    hCm = h * u;
  }
  if (!(L > 0)) return null;
  const lengthCm = L * u;
  const verticalExtent = shape === "stadium" ? Math.min(wCm, hCm) : hCm;
  const maxFillCm = orientation === "vertical" ? lengthCm : verticalExtent;
  return { shape, wCm, hCm, lengthCm, maxFillCm, orientation };
}

export function capacityCm3(g) {
  return fullCrossArea(g.shape, g.wCm, g.hCm) * g.lengthCm;
}

export function liquidCm3(g, fillCm) {
  const h = clamp(fillCm, 0, g.maxFillCm);
  if (g.orientation === "vertical") return fullCrossArea(g.shape, g.wCm, g.hCm) * h;
  return filledCrossArea(g.shape, g.wCm, g.hCm, h) * g.lengthCm;
}

// ---- calibration (linearisation) ----
// points: [{ level, liters }] in fixed unit (cm) & liters. Returns cleaned, sorted, valid pairs.
export function cleanCalibration(points) {
  return (points || [])
    .map((p) => ({ level: parseFloat(p.level), liters: parseFloat(p.liters) }))
    .filter((p) => Number.isFinite(p.level) && Number.isFinite(p.liters))
    .sort((a, b) => a.level - b.level);
}

function lerp(x, x0, y0, x1, y1) {
  if (x1 === x0) return y0;
  return y0 + ((y1 - y0) * (x - x0)) / (x1 - x0);
}

// interpolate liters at a given level (clamped to endpoints outside range)
export function interpolateVolume(points, level) {
  const pts = cleanCalibration(points);
  if (pts.length === 0) return 0;
  if (level <= pts[0].level) return pts[0].liters;
  if (level >= pts[pts.length - 1].level) return pts[pts.length - 1].liters;
  for (let i = 0; i < pts.length - 1; i++) {
    if (level >= pts[i].level && level <= pts[i + 1].level) {
      return lerp(level, pts[i].level, pts[i].liters, pts[i + 1].level, pts[i + 1].liters);
    }
  }
  return pts[pts.length - 1].liters;
}

// reverse: interpolate level for a given liters (clamped)
export function interpolateLevel(points, liters) {
  const pts = cleanCalibration(points);
  if (pts.length === 0) return 0;
  if (liters <= pts[0].liters) return pts[0].level;
  if (liters >= pts[pts.length - 1].liters) return pts[pts.length - 1].level;
  for (let i = 0; i < pts.length - 1; i++) {
    if (liters >= pts[i].liters && liters <= pts[i + 1].liters) {
      return lerp(liters, pts[i].liters, pts[i].level, pts[i + 1].liters, pts[i + 1].level);
    }
  }
  return pts[pts.length - 1].level;
}
